import { yupResolver } from "@hookform/resolvers/yup";
import FormButtons, { FormButtonInterface } from "components/forms/buttons";
import { CancelButton, SubmitButton } from "components/forms/buttons.utils";
import Form from "components/forms/form";
import Checkbox from "components/forms/inputs/checkbox";
import Dropdown from "components/forms/inputs/dropdown";
import { getDatatypeOptions } from "components/forms/inputs/dropdown.utils";
import TextInput from "components/forms/inputs/text";
import Col from "components/grid/col";
import Grid from "components/grid/grid";
import Row from "components/grid/row";
import { FormSubmitFunction } from "lib/form";
import { createEmpty } from "lib/locale";
import {
  FieldOptionInterface,
  ListEntityField,
} from "models/list-entity-field";
import { FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocales } from "stores/hooks/locale";
import { useTranslate } from "stores/hooks/translation";
import * as yup from "yup";
import FieldOptionsTable from "./field-options-table";

interface EntityFieldFormProps {
  field?: ListEntityField;
  existingIdentifiers: string[];
  onSubmit: FormSubmitFunction;
  onClose: () => Promise<void>;
}

const EntityFieldForm: FunctionComponent<EntityFieldFormProps> = (props) => {
  const translate = useTranslate();
  const locales = useLocales();

  const {
    existingIdentifiers = [],
    onClose,
    onSubmit,

    // field to edit or default for new
    field = {
      name: createEmpty("Uusi kenttä", locales),
      identifier: "",
      datatype: "text",
      admin_only: false,
      hide_from_user: false,
      required: false,
      large: false,
      options: [],
    },
  } = props;

  const [fieldOptions, toggleFieldOptions] = useState<FieldOptionInterface[]>(
    field.options || [],
  );

  const schema = yup
    .object({
      name: yup.object().validateLocaleString(),
      identifier: yup.string().validateNotOneOf(
        existingIdentifiers.filter((s) => s !== ""),
        "Tunniste on jo olemassa",
      ),
      datatype: yup.string(),
      admin_only: yup.bool(),
      hide_from_user: yup.bool(),
      required: yup.bool(),
      large: yup.bool(),
      table: yup.bool(),
    })
    .required();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: field.name,
      identifier: field.identifier,
      datatype: field.datatype,
      admin_only: field.admin_only,
      hide_from_user: field.hide_from_user,
      required: field.required,
      large: field.large,
    },
  });

  const buttons: FormButtonInterface[] = [
    { ...CancelButton, onClick: onClose },
    { ...SubmitButton },
  ];

  const { getValues } = methods;
  const { datatype } = getValues();

  const handleSubmit = async (values: any) => {
    onSubmit({ ...values, options: fieldOptions });
  };

  return (
    <Form methods={methods} onSubmit={handleSubmit}>
      <Grid>
        <Row>
          <Col>
            <TextInput required label="Kentän nimi" name="name" translations />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInput
              uppercase
              label="Kentän tunniste"
              name="identifier"
              // can't check identifier for locked fields
              disabled={props.field?.locked}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Dropdown
              name="datatype"
              label="Tietotyyppi"
              options={getDatatypeOptions(translate)}
            />
          </Col>
        </Row>
        {datatype === "dropdown" && (
          <Row>
            <FieldOptionsTable
              field={field as any}
              options={fieldOptions || []}
              onUpdate={async (options) => {
                toggleFieldOptions(options);
              }}
            />
          </Row>
        )}
        <Row>
          <Col>
            <Checkbox name="required" label="Vastaaminen pakollista" />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="hide_from_user"
              label="Piilotettu ilmoitusvelvolliselta"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="admin_only"
              label="Vain ylläpitäjän muokattavissa"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox name="large" label="Leveä kenttä" />
          </Col>
        </Row>
        {/* options */}
        <FormButtons buttons={buttons} />
      </Grid>
    </Form>
  );
};

export default EntityFieldForm;
